import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import { SparklesIcon } from '@heroicons/react/24/outline';
import {
  calculateAllMarketplaceFees,
  DEPOP_REGIONS,
  EBAY_CATEGORY_OPTIONS,
  GRAILED_PAYMENT_OPTIONS,
  STOCKX_SELLER_LEVELS,
  type DepopRegion,
  type EbayCategoryType,
  type GrailedPaymentType,
  type MarketplaceFeeResult,
  type MarketplaceKey,
} from '../utils/feeCalculator';
import { trackCalculatorPageView, trackCalculatorCtaClicked } from '../utils/calculatorAnalytics';
import GrailedImg from '../images/logos/grailed.png';
import StockXImg from '../images/logos/stockx.svg';
import MercariImg from '../images/logos/mercari.png';
import DepopImg from '../images/logos/depop.svg';
import EbayImg from '../images/logos/ebay.svg';
import PoshmarkImg from '../images/logos/POSH.svg';

const SITE_URL = 'https://www.tracknlist.com';
const CALCULATOR_URL = `${SITE_URL}/calculator`;
const SOCIAL_PROOF_COUNT = '5,000+';

type CurrencyCode = 'USD' | 'GBP' | 'EUR' | 'CAD' | 'AUD';

const CURRENCIES: { code: CurrencyCode; symbol: string; label: string; perUsd: number }[] = [
  { code: 'USD', symbol: '$', label: 'USD', perUsd: 1 },
  { code: 'GBP', symbol: '£', label: 'GBP', perUsd: 0.79 },
  { code: 'EUR', symbol: '€', label: 'EUR', perUsd: 0.92 },
  { code: 'CAD', symbol: 'C$', label: 'CAD', perUsd: 1.36 },
  { code: 'AUD', symbol: 'A$', label: 'AUD', perUsd: 1.53 },
];

const MARKETPLACE_ICONS: Record<MarketplaceKey, string> = {
  GRAILED: GrailedImg,
  STOCKX: StockXImg,
  MERCARI: MercariImg,
  DEPOP: DepopImg,
  EBAY: EbayImg,
  POSHMARK: PoshmarkImg,
};

const HERO_MARKETPLACES: { key: MarketplaceKey; name: string; icon: string }[] = [
  { key: 'EBAY', name: 'eBay', icon: EbayImg },
  { key: 'STOCKX', name: 'StockX', icon: StockXImg },
  { key: 'GRAILED', name: 'Grailed', icon: GrailedImg },
  { key: 'POSHMARK', name: 'Poshmark', icon: PoshmarkImg },
];

const FAQ_ITEMS: { q: string; a: string }[] = [
  { q: 'What fees does eBay charge resellers?', a: 'eBay charges a final value fee (typically 13.6% on the first $7,500, then 2.35% on the rest for most categories) plus a per-order fee ($0.30 or $0.40). Category-specific rates apply for sneakers, trading cards, jewelry, and more.' },
  { q: 'How do StockX fees work?', a: 'StockX charges a 3% payment processing fee, a transaction fee (9% at Level 1, down to 7% at Level 5), and a $4 shipping fee. The minimum total fee is $5 for US sellers.' },
  { q: 'What are Grailed and Poshmark seller fees?', a: 'Grailed charges 9% transaction fee plus Stripe payment processing (3.49% + $0.49 for US domestic). Poshmark charges $2.95 flat for sales under $15, or 20% for $15 and above.' },
  { q: 'How do I calculate my true profit margin?', a: 'True profit = sale price minus marketplace fees minus item cost minus shipping. Use our calculator to compare payouts across platforms, then subtract your acquisition and shipping costs to see net profit.' },
  { q: 'Do marketplace fees vary by country?', a: 'Yes. Depop has different payment processing rates for US (3.3% + $0.45), UK (2.9% + £0.30), and other regions. Grailed fees vary by Stripe eligibility. Our calculator supports multi-currency (USD, GBP, EUR, CAD, AUD) for international resellers.' },
  { q: 'What about international shipping costs?', a: 'Marketplace fees are calculated on the sale price. Shipping costs are separate—either you absorb them, pass them to the buyer, or use marketplace shipping programs. Add your out-of-pocket shipping in the calculator to see net profit.' },
];

const MARKETPLACE_COMPARISON: { name: string; feeRange: string; keyFee: string }[] = [
  { name: 'eBay', feeRange: '~8 - 15% + $0.30 - 0.40', keyFee: 'Category-based FVF' },
  { name: 'StockX', feeRange: '~12 - 14% + $4', keyFee: 'Level-based transaction %' },
  { name: 'Grailed', feeRange: '~12.5 - 14.5%', keyFee: '9% + Stripe processing' },
  { name: 'Poshmark', feeRange: '20% or $2.95', keyFee: 'Flat above $15' },
  { name: 'Mercari', feeRange: '10%', keyFee: 'Simple 10% selling fee' },
  { name: 'Depop', feeRange: '~13% + boost', keyFee: '10% + payment processing' },
];

const CALCULATOR_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Free Reseller Profit Calculator',
  applicationCategory: 'FinanceApplication',
  operatingSystem: 'Web',
  description: 'Calculate your true profit after eBay, StockX, Grailed, Poshmark, Mercari, and Depop fees. Multi-currency support for USD, GBP, EUR, CAD, and AUD.',
  url: CALCULATOR_URL,
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
};

const BREADCRUMB_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
    { '@type': 'ListItem', position: 2, name: 'Free Reseller Profit Calculator', item: CALCULATOR_URL },
  ],
};

function buildFaqSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  };
}

const Calculator: React.FC = () => {
  const [sellPriceInput, setSellPriceInput] = useState('');
  const [itemCostInput, setItemCostInput] = useState('');
  const [shippingInput, setShippingInput] = useState('');
  const [currency, setCurrency] = useState<CurrencyCode>('USD');
  const [results, setResults] = useState<MarketplaceFeeResult[]>([]);
  const [expandedRow, setExpandedRow] = useState<MarketplaceKey | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [stockxSellerLevel, setStockxSellerLevel] = useState(1);
  const [depopRegion, setDepopRegion] = useState<DepopRegion>('US');
  const [depopBoosted, setDepopBoosted] = useState(false);
  const [grailedPaymentType, setGrailedPaymentType] = useState<GrailedPaymentType>('US_DOMESTIC');
  const [ebayCategoryType, setEbayCategoryType] = useState<EbayCategoryType>('MOST_CATEGORIES');

  const currencyInfo = CURRENCIES.find(c => c.code === currency) ?? CURRENCIES[0];
  const sellPriceLocal = parseFloat(sellPriceInput);
  const sellPriceUsd = sellPriceLocal / currencyInfo.perUsd;
  const isValidPrice = Number.isFinite(sellPriceUsd) && sellPriceUsd >= 0;
  const itemCost = parseFloat(itemCostInput) || 0;
  const shippingCost = parseFloat(shippingInput) || 0;

  const formatCurrency = useCallback(
    (usdAmount: number) => {
      const localAmount = usdAmount * currencyInfo.perUsd;
      return `${currencyInfo.symbol}${localAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    [currencyInfo]
  );

  useEffect(() => trackCalculatorPageView(), []);
  useEffect(() => {
    if (!isValidPrice) {
      setResults([]);
      return;
    }
    setResults(
      calculateAllMarketplaceFees(sellPriceUsd, {
        stockxSellerLevel,
        depopRegion,
        depopBoosted,
        grailedPaymentType,
        ebayCategoryType,
      })
    );
  }, [sellPriceUsd, isValidPrice, stockxSellerLevel, depopRegion, depopBoosted, grailedPaymentType, ebayCategoryType]);

  const bestPayout = results.length ? results[0].payout : null;
  const netProfit = useCallback(
    (payoutUsd: number) => Math.max(0, payoutUsd - itemCost / currencyInfo.perUsd - shippingCost / currencyInfo.perUsd),
    [itemCost, shippingCost, currencyInfo.perUsd]
  );

  const handleCtaClick = (location: string) => {
    trackCalculatorCtaClicked({ cta_location: location });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Helmet>
        {/* Title/description/canonical/og/twitter are in server-patched HTML for /calculator. Only add what is not in the initial HTML. */}
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <script type="application/ld+json">{JSON.stringify(CALCULATOR_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(buildFaqSchema())}</script>
      </Helmet>

      <main className="mx-auto max-w-4xl px-4 sm:px-6 py-12 sm:py-16 mt-16">
        {/* Hero */}
        <section className="mb-10 sm:mb-12 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">
            Free Reseller Profit Calculator
          </h1>
          <p className="text-xl sm:text-2xl text-gray-600 dark:text-gray-400 mb-2">Know your profit before you list</p>
          <p className="text-base text-gray-600 dark:text-gray-500 mb-8 max-w-2xl mx-auto">
            Compare payout estimates across eBay, StockX, Grailed, Poshmark, Mercari & Depop. Multi-currency support for USD, GBP, EUR, CAD & AUD.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8 mb-6">
            {HERO_MARKETPLACES.map(({ name, icon }) => (
              <div key={name} className="flex items-center gap-2 h-10 px-4 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm">
                <img src={icon} alt={`${name} marketplace logo`} className="h-6 w-auto object-contain opacity-90" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{name}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Used by {SOCIAL_PROOF_COUNT} resellers</p>
        </section>

        {/* Calculator card */}
        <section className="mb-12">
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg p-6 sm:p-8">
            <h2 className="sr-only">Profit calculator inputs</h2>
            <div className="mb-6">
              <label htmlFor="sell-price" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
                What price will you list it for?
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Enter the sale price buyers will pay</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1 max-w-[280px]">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 font-medium">{currencyInfo.symbol}</span>
                  <input
                    id="sell-price"
                    type="number"
                    inputMode="decimal"
                    placeholder="e.g. 200"
                    value={sellPriceInput}
                    onChange={e => setSellPriceInput(e.target.value.replace(/[^0-9.]/g, ''))}
                    className="block w-full h-12 pl-10 pr-4 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder:text-gray-400 font-semibold text-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>
                <select
                  value={currency}
                  onChange={e => setCurrency(e.target.value as CurrencyCode)}
                  className="h-12 px-4 w-36 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  aria-label="Currency"
                >
                  {CURRENCIES.map(c => (
                    <option key={c.code} value={c.code}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="pt-6 border-t border-gray-200 dark:border-gray-600">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">Your costs — subtract these to see net profit</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Optional. Leave blank for payout only.</p>
              <div className="flex flex-wrap gap-6">
                <div>
                  <label htmlFor="item-cost" className="block text-sm text-gray-600 dark:text-gray-400 mb-1">What you paid for the item</label>
                  <div className="relative w-32">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 text-sm">{currencyInfo.symbol}</span>
                    <input
                      id="item-cost"
                      type="number"
                      inputMode="decimal"
                      placeholder="0"
                      value={itemCostInput}
                      onChange={e => setItemCostInput(e.target.value.replace(/[^0-9.]/g, ''))}
                      className="w-full h-10 pl-8 pr-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="shipping" className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Your shipping cost</label>
                  <div className="relative w-32">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 text-sm">{currencyInfo.symbol}</span>
                    <input
                      id="shipping"
                      type="number"
                      inputMode="decimal"
                      placeholder="0"
                      value={shippingInput}
                      onChange={e => setShippingInput(e.target.value.replace(/[^0-9.]/g, ''))}
                      className="w-full h-10 pl-8 pr-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Results table */}
        {results.length > 0 && (
          <section className="mb-12" aria-label="Fee comparison results">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Itemized fee breakdown</h2>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Sell price: <span className="font-semibold text-gray-700 dark:text-gray-200">{formatCurrency(sellPriceUsd)}</span>
              </span>
            </div>
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                      <th scope="col" className="py-3.5 pl-5 pr-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Marketplace</th>
                      <th scope="col" className="py-3.5 px-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Fees</th>
                      <th scope="col" className="py-3.5 px-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Payout</th>
                      {(itemCost > 0 || shippingCost > 0) && (
                        <th scope="col" className="py-3.5 px-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Net profit</th>
                      )}
                      <th scope="col" className="w-24 py-3.5 pr-4 text-right" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {results.map(row => {
                      const isBest = bestPayout !== null && row.payout === bestPayout;
                      const isExpanded = expandedRow === row.marketplace;
                      const profit = netProfit(row.payout);
                      return (
                        <React.Fragment key={row.marketplace}>
                          <tr className={isBest ? 'bg-emerald-50 dark:bg-emerald-900/20 border-l-4 border-l-emerald-500' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}>
                            <td className="py-4 pl-5 pr-3">
                              <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 shadow-sm">
                                  <img src={MARKETPLACE_ICONS[row.marketplace]} alt={`${row.name} logo`} className="h-5 w-5 object-contain" />
                                </div>
                                <span className="font-semibold text-gray-900 dark:text-white">{row.name}</span>
                                {isBest && <span className="inline-flex rounded-full bg-emerald-500 px-2.5 py-0.5 text-xs font-bold text-white">Best</span>}
                              </div>
                            </td>
                            <td className="py-4 px-3 text-right font-medium text-red-600 dark:text-red-400">−{formatCurrency(row.fee)}</td>
                            <td className="py-4 px-3 text-right">
                              <span className="font-bold text-gray-900 dark:text-white tabular-nums">{formatCurrency(row.payout)}</span>
                            </td>
                            {(itemCost > 0 || shippingCost > 0) && (
                              <td className="py-4 px-3 text-right">
                                <span className="font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">{formatCurrency(profit)}</span>
                              </td>
                            )}
                            <td className="py-4 pr-5 text-right">
                              {row.breakdown && (
                                <button
                                  type="button"
                                  onClick={() => setExpandedRow(isExpanded ? null : row.marketplace)}
                                  className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                  aria-expanded={isExpanded}
                                >
                                  {isExpanded ? <><ChevronUpIcon className="h-4 w-4" /> Hide</> : <><ChevronDownIcon className="h-4 w-4" /> Details</>}
                                </button>
                              )}
                            </td>
                          </tr>
                          {isExpanded && row.breakdown && (
                            <tr className={isBest ? 'bg-emerald-50/60 dark:bg-emerald-900/10' : 'bg-gray-50 dark:bg-gray-700/30'}>
                              <td colSpan={itemCost > 0 || shippingCost > 0 ? 5 : 4} className="px-5 py-4 text-sm text-gray-600 dark:text-gray-400 border-t border-gray-200 dark:border-gray-600">
                                <p className="mb-3">
                                  <span className="font-semibold text-gray-700 dark:text-gray-200">{row.name} fee breakdown</span>
                                  <span className="ml-2">—</span>
                                  <span className="ml-2">{row.breakdown}</span>
                                </p>
                                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 space-y-4">
                                  {row.marketplace === 'GRAILED' && (
                                    <div>
                                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Payment processing</p>
                                      <div className="flex flex-wrap gap-2">
                                        {GRAILED_PAYMENT_OPTIONS.map(({ value, label }) => (
                                          <button
                                            key={value}
                                            type="button"
                                            onClick={() => setGrailedPaymentType(value)}
                                            className={`rounded-lg px-3 py-1.5 text-xs font-medium ${grailedPaymentType === value ? 'bg-indigo-600 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'}`}
                                          >
                                            {label}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {row.marketplace === 'STOCKX' && (
                                    <div>
                                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Seller level</p>
                                      <div className="flex flex-wrap gap-2">
                                        {STOCKX_SELLER_LEVELS.map(({ level, transactionPct }) => (
                                          <button
                                            key={level}
                                            type="button"
                                            onClick={() => setStockxSellerLevel(level)}
                                            className={`rounded-lg px-3 py-1.5 text-xs font-medium ${stockxSellerLevel === level ? 'bg-indigo-600 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'}`}
                                          >
                                            Level {level} ({transactionPct}%)
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {row.marketplace === 'DEPOP' && (
                                    <div className="flex flex-wrap items-center gap-6">
                                      <div>
                                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Region</p>
                                        <div className="flex flex-wrap gap-2">
                                          {DEPOP_REGIONS.map(({ value, label }) => (
                                            <button
                                              key={value}
                                              type="button"
                                              onClick={() => setDepopRegion(value)}
                                              className={`rounded-lg px-3 py-1.5 text-xs font-medium ${depopRegion === value ? 'bg-indigo-600 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'}`}
                                            >
                                              {label}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                      <label className="flex cursor-pointer items-center gap-2.5 text-sm text-gray-600 dark:text-gray-400">
                                        <input type="checkbox" checked={depopBoosted} onChange={e => setDepopBoosted(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                                        Sold via boost (+8%)
                                      </label>
                                    </div>
                                  )}
                                  {row.marketplace === 'EBAY' && (
                                    <div>
                                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Category</p>
                                      <select
                                        value={ebayCategoryType}
                                        onChange={e => setEbayCategoryType(e.target.value as EbayCategoryType)}
                                        className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 w-full max-w-sm"
                                      >
                                        {EBAY_CATEGORY_OPTIONS.map(opt => (
                                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                      </select>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {sellPriceInput !== '' && !isValidPrice && <p className="mb-6 text-sm text-amber-600 dark:text-amber-400">Enter a valid price (e.g. 200 or 99.99).</p>}

        {/* CTA */}
        {results.length > 0 && (
          <section className="mb-12 text-center">
            <div className="rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-600 p-8 text-white">
              <p className="text-lg font-semibold mb-2">Save your calculations & automate listings</p>
              <p className="text-indigo-100 text-sm mb-4 max-w-md mx-auto">
                Create a free account to track inventory, cross-list to all marketplaces, and never calculate fees manually again.
              </p>
              <Link
                to="/signup"
                onClick={() => handleCtaClick('below_calculator')}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-indigo-600 font-semibold hover:bg-indigo-50 transition-colors shadow-lg"
              >
                Create free account
              </Link>
            </div>
          </section>
        )}

        {/* FAQ */}
        <section className="mb-12" aria-label="Frequently asked questions">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Frequently asked questions</h2>
          <div className="space-y-2">
            {FAQ_ITEMS.map((item, i) => (
              <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left text-gray-900 dark:text-white font-semibold hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  aria-expanded={expandedFaq === i}
                >
                  {item.q}
                  <ChevronDownIcon className={`h-5 w-5 shrink-0 transition-transform ${expandedFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {expandedFaq === i && (
                  <div className="px-5 pb-4 text-gray-600 dark:text-gray-400 text-sm border-t border-gray-100 dark:border-gray-700 pt-3">{item.a}</div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Marketplace comparison */}
        <section className="mb-12" aria-label="Marketplace fee comparison">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Marketplace fee comparison</h2>
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                    <th scope="col" className="py-3.5 pl-5 pr-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Marketplace</th>
                    <th scope="col" className="py-3.5 px-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Typical fee range</th>
                    <th scope="col" className="py-3.5 px-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Key fee structure</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {MARKETPLACE_COMPARISON.map(row => (
                    <tr key={row.name}>
                      <td className="py-4 pl-5 pr-3 font-semibold text-gray-900 dark:text-white">{row.name}</td>
                      <td className="py-4 px-3 text-gray-600 dark:text-gray-400">{row.feeRange}</td>
                      <td className="py-4 px-3 text-gray-600 dark:text-gray-400">{row.keyFee}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="mb-8">
          <div className="rounded-2xl bg-indigo-50 dark:bg-gray-800 border border-indigo-100 dark:border-gray-700 p-8 sm:p-12 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">
              Stop Calculating Manually — Automate Your Entire Workflow
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-xl mx-auto">
              TrackNList helps resellers manage inventory, cross-list to eBay, StockX, Grailed, Poshmark & more, and track true profit—all in one place.
            </p>
            <Link
              to="/signup"
              onClick={() => handleCtaClick('final_section')}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-lg transition-colors shadow-lg"
            >
              <SparklesIcon className="h-6 w-6" />
              Get started free
            </Link>
          </div>
        </section>

        <p className="text-center text-xs text-gray-500 dark:text-gray-400">
          Fees are estimates for US sellers. Actual fees may vary by category, region, and promotions.
        </p>
      </main>
    </div>
  );
};

export default Calculator;
